import { type FC, type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { LinkItem, PagedResultOfLinkItem } from "src/http/types";
import { SiteErrorBoundary } from "src/shared/components/SiteErrorBoundary";
import { useAppContext } from "src/shared/context/AppContext";
import { useFetchSender } from "src/shared/hooks/useFetchSender";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { BoundaryNames } from "src/shared/utils/sentry/BoundaryNames";
import { SentrySeverityLevel } from "src/shared/utils/sentry/SentrySeverityLevel";
import { writeUrl } from "src/shared/utils/url-builder";
import { LinksGrid } from "src/website-apps/shared/components/LinksGrid";
import { PageNumbers } from "src/website-apps/shared/components/PageNumbers";

const PAGE_MAX = 40;

interface Props {
  title: ReactNode;
  initLinks: readonly LinkItem[];
  totalCount: number;
  venueId: number;
  method: string;
}

const LinksGridManagerComp: FC<Props> = ({ title, initLinks, totalCount, venueId, method }) => {
  const appContext = useAppContext();
  const fetchSender = useFetchSender();
  const [page, setPage] = useState(0);
  const numPages = Math.ceil(totalCount / PAGE_MAX);

  const { data } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.VENUE_LINKS, { venueId, page }),
    queryFn: async ({ signal }) => {
      const response = await fetchSender<{ data: PagedResultOfLinkItem }>(
        writeUrl("", null, appContext),
        {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            method,
            venueId,
            index: page,
          }),
          signal,
        },
        [],
      );
      return response.data;
    },
    enabled: page > 0, // Only fetch for non-initial pages
    placeholderData: (previousData) => previousData, // Keep previous data while loading
    initialData: page === 0 ? { items: initLinks, totalCount: initLinks.length } : undefined,
    staleTime: STALE_TIME_ONE_HOUR,
  });

  const links = data?.items ?? initLinks;

  return (
    <>
      <LinksGrid title={title} links={links} />
      {totalCount > PAGE_MAX && <PageNumbers page={page} setPage={setPage} numPages={numPages} />}
    </>
  );
};

const getLinksGridInfo = ({ venueId }: Pick<Props, "venueId">) => ({
  venueId,
});

export const LinksGridManager: FC<Props> = ({ title, initLinks, totalCount, venueId, method }) => {
  const getErrorInfo = () => getLinksGridInfo({ venueId });
  return (
    <SiteErrorBoundary
      boundaryName={BoundaryNames.LINKS_GRID_MANAGER}
      getErrorInfo={getErrorInfo}
      severity={SentrySeverityLevel.INFO}
    >
      <LinksGridManagerComp
        title={title}
        initLinks={initLinks}
        totalCount={totalCount}
        venueId={venueId}
        method={method}
      />
    </SiteErrorBoundary>
  );
};
